export function joinPromptAndTranscript(base: string, transcript: string): string {
  const nextTranscript = transcript.trim();
  if (nextTranscript.length === 0) {
    return base;
  }
  if (base.length === 0) {
    return nextTranscript;
  }
  if (/\s$/u.test(base)) {
    return `${base}${nextTranscript}`;
  }
  return `${base} ${nextTranscript}`;
}

export function collectFinalSpeechTranscript(event: SpeechRecognitionResultEvent): string {
  let transcript = "";
  for (let index = event.resultIndex; index < event.results.length; index += 1) {
    const result = event.results[index];
    if (!result?.isFinal) {
      continue;
    }
    transcript += result[0]?.transcript ?? "";
  }
  return transcript;
}

/**
 * The in-progress (not-yet-final) transcript for live "keep writing while I
 * speak" feedback. Interim results are replaced as the recognizer refines them,
 * so this is display-only — commit `collectFinalSpeechTranscript` to the prompt.
 */
export function collectInterimSpeechTranscript(event: SpeechRecognitionResultEvent): string {
  let transcript = "";
  for (let index = event.resultIndex; index < event.results.length; index += 1) {
    const result = event.results[index];
    if (!result || result.isFinal) {
      continue;
    }
    transcript += result[0]?.transcript ?? "";
  }
  return transcript;
}

export type DictationErrorKey =
  | "composer.dictationUnsupported"
  | "composer.dictationBlocked"
  | "composer.dictationMicDenied"
  | "composer.dictationNoMic"
  | "composer.dictationMicBusy"
  | "composer.dictationNetwork"
  | "composer.dictationInsecure"
  | "composer.dictationFailed";

export function speechRecognitionErrorKey(error: string): DictationErrorKey | null {
  switch (error) {
    case "not-allowed":
      return "composer.dictationMicDenied";
    case "audio-capture":
      return "composer.dictationNoMic";
    case "network":
      return "composer.dictationNetwork";
    case "service-not-allowed":
    case "language-not-supported":
      return "composer.dictationBlocked";
    case "aborted":
    case "no-speech":
      return null;
    default:
      return "composer.dictationFailed";
  }
}

export function microphoneAccessErrorKey(cause: unknown): DictationErrorKey {
  const name = cause instanceof DOMException ? cause.name : "";
  const message =
    cause instanceof Error ? cause.message.toLowerCase() : String(cause).toLowerCase();
  if (
    name === "NotAllowedError" ||
    name === "PermissionDeniedError" ||
    message.includes("not allowed") ||
    message.includes("permission denied")
  ) {
    return "composer.dictationMicDenied";
  }
  if (name === "NotFoundError" || name === "DevicesNotFoundError") {
    return "composer.dictationNoMic";
  }
  if (
    name === "NotReadableError" ||
    name === "TrackStartError" ||
    name === "AbortError" ||
    message.includes("could not start audio")
  ) {
    return "composer.dictationMicBusy";
  }
  if (name === "SecurityError") {
    return "composer.dictationInsecure";
  }
  return "composer.dictationFailed";
}

type MicrophoneTrack = { stop: () => void };
type MicrophoneStream = { getTracks: () => MicrophoneTrack[] };

export async function ensureDictationMicrophoneAccess(options?: {
  isSecureContext?: boolean;
  getUserMedia?: (constraints: MediaStreamConstraints) => Promise<MicrophoneStream>;
}): Promise<DictationErrorKey | null> {
  const isSecureContext = options?.isSecureContext ?? window.isSecureContext;
  if (!isSecureContext) {
    return "composer.dictationInsecure";
  }

  const getUserMedia =
    options?.getUserMedia ?? navigator.mediaDevices?.getUserMedia?.bind(navigator.mediaDevices);
  if (!getUserMedia) {
    return null;
  }

  try {
    const stream = await getUserMedia({ audio: true });
    for (const track of stream.getTracks()) {
      track.stop();
    }
    return null;
  } catch (cause) {
    return microphoneAccessErrorKey(cause);
  }
}

export type SpeechRecognitionResultEvent = {
  resultIndex: number;
  results: ArrayLike<{ isFinal: boolean; 0?: { transcript?: string } }>;
};

type SpeechRecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: SpeechRecognitionResultEvent) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
};

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

export function getSpeechRecognitionConstructor(): SpeechRecognitionConstructor | null {
  if (typeof window === "undefined") {
    return null;
  }
  const speechWindow = window as Window & {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  };
  return speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition ?? null;
}

/**
 * Electron's Chromium exposes `webkitSpeechRecognition` but cannot reach
 * Google's speech service, so the first event is always `error: "network"`.
 * Firefox has no Web Speech API. In both cases we record locally and
 * transcribe through `/api/morrow/dictation`.
 */
export function shouldPreferServerDictation(input: {
  isElectron: boolean;
  hasSpeechRecognition: boolean;
}): boolean {
  return input.isElectron || !input.hasSpeechRecognition;
}

export function whisperLanguageFromLocale(locale: string | undefined): string | undefined {
  if (!locale) return undefined;
  const base = locale.trim().toLowerCase().split("-")[0] ?? "";
  return /^[a-z]{2}$/u.test(base) ? base : undefined;
}

const DICTATION_RECORDER_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
] as const;

export function pickDictationRecorderMimeType(
  isTypeSupported: (type: string) => boolean = (type) =>
    typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(type),
): string | undefined {
  return DICTATION_RECORDER_TYPES.find((type) => isTypeSupported(type));
}

/**
 * Cloudflare Whisper is a batch model — there is no token-streaming transcript
 * API. To still give the hosted-web (server) dictation path the live "words
 * appear as you speak" feedback the Web Speech path has, we record in short
 * rolling segments:
 *
 *   - The MediaRecorder runs continuously with a `DICTATION_INTERIM_MS`
 *     timeslice, so `ondataavailable` fires roughly every second WITHOUT
 *     stopping the recorder (no dropped audio). After each slice we transcribe
 *     the cumulative segment-so-far and show it as an interim preview.
 *   - Every `DICTATION_SEGMENT_MS` we stop → commit the segment's final
 *     transcript into the composer, and immediately open the next segment. The
 *     only audio gap is the sub-frame stop/start at that boundary.
 *
 * Cumulative interim audio stays bounded by the segment length, so interim
 * transcription cost/latency never grows without limit during long dictation.
 */
// MediaRecorder timeslice; also the interim-preview cadence.
export const DICTATION_INTERIM_MS = 1000;
// Max length of a segment before we commit its transcript and rotate.
export const DICTATION_SEGMENT_MS = 6000;
export const DICTATION_MIN_CHUNK_BYTES = 1000;
